<template>
    <div class="">
        <install-wizard-card>
            <template #top-header>Layout</template>
            <template #top-content>
                <p>
                    These will help you define your overall dashboard layout!
                </p>
                <p>
                    Areas are the largest unit of organization. These generally are separate physical locations. An Area may contain multiple Zones.
                </p>
                <p>
                    Zones hold similar points (ex. Pharmacy Freezers may be one zone; Cafeteria Coolers may be another).
                </p>
                <p>
                    Groups help determine who can see what; add groups to Zones that you want members to view.
                </p>
            </template>

            <template #body-summary>
                <div class="flex flex-wrap ml-6">
                    <div class="w-full sm:w-3/4 mr-0 sm:mr-4">Areas and zones can be modified later, but this'll give you a head start.</div>
                </div>
            </template>

            <template #body-action>
                <install-wizard-well v-for="(area, areaIndex) in areas" :key="areaIndex" :expanded="true">
                    <template #well-header-left>
                        <div>
                            <div    @click.stop
                                    class="w-3/4 md:w-3/4">
                                <floating-input     :id="'area_name_' + area.ID"
                                                    v-model:value="area.Name"
                                                    label="Area Name"
                                                    @input="area.needsSaved = true"
                                ></floating-input>
                            </div>
                            <span :class="area.needsSaved ? 'icon-sync-crossed' : ''" class="pl-2 w-2 lnr"></span>
                        </div>
                        <div class="flex flex-wrap items-center mt-2 text-sm leading-5 text-gray-500">
                            <div v-for="(zone, zoneIndex) in area.zones"
                                 class="w-full">
                                <span :class="zone.needsSaved ? 'icon-sync-crossed' : 'icon-check'" class="w-2 lnr"></span>
                                {{zone.Name}}
                            </div>
                        </div>
                    </template>
                    <template #well-header-center>
                        <div class="grid sm:grid-cols-1 lg:grid-cols-2">
                            <button @click.stop="updateArea(area)"
                                    class="w-3/4 lg:w-full xl:w-3/4 items-center ml-2 px-4 py-2 border-2 border-blue-200 text-sm leading-5 font-sm font-sans rounded-md text-blue-700 bg-white-600 hover:bg-blue-200 focus:outline-none focus:ring-blue-200 focus:border-blue-400 active:bg-blue-300">
                                Save
                            </button>
                            <button @click.stop="deleteArea(areaIndex)"
                                    class="w-3/4 lg:w-full xl:w-3/4 items-center ml-2 px-4 py-2 border-2 border-red-200 text-sm leading-5 font-sm font-sans rounded-md text-red-700 bg-white-600 hover:bg-red-200 focus:outline-none focus:ring-red-200 focus:border-red-400 active:bg-red-300">
                                Delete
                            </button>
                        </div>
                    </template>
                    <template #well-content>
                        <ul class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-2">
                            <install-wizard-layout-card
                                    v-for="(zone, zoneIndex) in area.zones"
                                    :zone-prop="zone"
                                    :zone-index="zoneIndex"
                                    :area-index="areaIndex"
                                    :groups="groups"
                                    :key="`zone-${area.ID}-${zoneIndex}`"
                                    :group-alarming-enabled="groupAlarmingEnabled"
                                    v-on:deleted="deleteZone"
                            ></install-wizard-layout-card>
                            <!-- Add option to add new Zone -->
                            <li class="flex flex-col col-span-1 text-center bg-white rounded-lg shadow justify-center">
                                <div class="flex -mt-px">
                                    <div class="flex flex-1 w-0 border-gray-200">
                                        <div @click="addZone(areaIndex)"
                                             class="relative inline-flex items-center justify-center flex-1 w-0 py-4 text-sm font-medium leading-5 text-gray-700 border border-transparent rounded-bl-lg cursor-pointer hover:text-gray-500 focus:outline-none focus:ring-blue-200 focus:border-blue-300 focus:z-10">
                                            <span class="lnr icon-plus"></span>
                                            <span class="ml-3">Add another Zone</span>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </template>
                </install-wizard-well>
                <!-- Offer the opportunity to add a new Area -->
                <install-wizard-well>
                    <template #well-header-left>
                        <div class="w-3/4">
                            <floating-input     v-model:value="newAreaName"
                                                label="New Area Name"
                                                id="newAreaName"
                            ></floating-input>
                        </div>
                    </template>
                    <template #well-header-center>
                        <button @click="addArea"
                                :disabled="newAreaName.length === 0"
                                class="items-center ml-2 px-4 py-2 border-2 border-blue-200 text-sm leading-5 font-sm font-sans rounded-md text-blue-700 bg-white-600 hover:bg-blue-200 focus:outline-none focus:ring-blue-200 focus:border-blue-400 active:bg-blue-300">
                            Create new Area
                        </button>
                    </template>

                    <template #well-header-right><span></span></template>
                </install-wizard-well>
            </template>

            <template #bottom-header>Done with Areas and Zones?</template>
            <!--            <template #bottom-button-text></template>-->
            <template #bottom-button>
                <a>
                    <button type="button"
                            @click="completeStepAreas"
                            class="relative inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-blue-200 focus:border-blue-700 active:bg-blue-700">
                        <slot name="bottom-button-text">Save Layout!</slot>
                    </button>
                </a>
            </template>
        </install-wizard-card>
    </div>
</template>

<script>
    import axios from 'axios';
    import InstallWizardCard from "./InstallWizardCard";
    import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
    import InstallWizardLayoutCard from "@/components/InstallWizard/InstallWizardLayoutCard";
    import InstallWizardWell from "@/components/InstallWizard/InstallWizardWell";
    import Select2 from "@/components/elements/select2"
    import FloatingInput from "@/components/elements/FloatingInput"

    export default {
        name: "InstallWizardLayout",
        mixins: [SonicuCoreMixin],
        props: {
            initialAreas: Array,
            groups: Object,
            groupAlarmingEnabled: Boolean,
        },
        mounted(){
            this.areas.forEach((area) => {
              area['needsSaved'] = false
                area.zones.forEach((zone) => {
                  zone['needsSaved'] = false
                })
            })
        },
        data(){
            return {
                areas: this.initialAreas,
                newAreaName: '',
                saved: false,
            }
        },
        components: {InstallWizardWell, InstallWizardLayoutCard, InstallWizardCard, Select2, FloatingInput},
        methods: {
            addZone(areaIndex){
                this.areas[areaIndex].zones.push({
                    Name: '',
                    Groups: "1,",
                    AreaID: this.areas[areaIndex].ID,
                });
            },
            deleteZone(areaIndex, zoneIndex){
                this.areas[areaIndex].zones.splice(zoneIndex, 1);
            },
            addArea(){
                axios
                    .post(this.company.buildCompanyApiInternalRoute('wizard/areas/'),
                        {name: this.newAreaName})
                    .then(response => {
                        this.newAreaName = '';
                        this.areas.push(response.data);
                    });
            },
            updateArea(area){
                axios
                    .put(this.company.buildCompanyApiInternalRoute('wizard/areas/'+String(area.ID)+'/edit'),
                        {name: area.Name})
                    .then(response => {
                        area.needsSaved = false;
                    });
            },
            deleteArea(areaIndex){
                let areaId = this.areas[areaIndex].ID
                this.areas.splice(areaIndex, 1);
                axios
                    .delete(this.company.buildCompanyApiInternalRoute('wizard/areas/'+String(areaId)),
                        {})
                    .then(response => {
                    });
            },
            saveInfo(){
                axios.post(this.company.buildCompanyApiInternalRoute('areas-zones/'), this.areas)
                .then((response) => {
                    this.saved = true;
                    setTimeout(() =>{
                        // Update so that we can be sure we
                        this.saved = false;
                    }, 6000);
                    return true;
                });
            },
            completeStepAreas() {
                console.log(this.areas);
                axios.post(window.location.pathname, this.areas)
                    .then(function (response) {
                        window.location.href = response.data.target;
                    });
            }
        }
    }
</script>

<style scoped>
    .fade-enter-active, .fade-leave-active{
        @apply transition-all duration-300 ease-in-out;
    }
    .fade-enter, .fade-leave-to {
        @apply opacity-0 scale-75;
    }

    .fade-leave, .fade-enter-to{
        @apply opacity-100 scale-100;
    }
</style>